import { API, Auth } from "aws-amplify";
import { doWhilst } from "async";

import { QuoteResponse } from "./model/QuoteResponse";
import { Order } from "./model/Order";
import { Page } from "./model/Page";
import { Invoice } from "./model/Invoice";
import { Address } from "./model/Address";
import { QuoteRequest } from "./model/QuoteRequest";
import { CreateOrderRequest } from "./model/CreateOrderRequest";
import { LiveTrackingLocation } from "./model/LiveTrackingLocation";
import { UploadResponse } from "./model/UploadResponse";
import { CheckZipResponse } from "./model/CheckZipResponse";
import { OrdersFilter } from "./model/OrdersFilter";
import { InvoicesFilter } from "./model/InvoicesFilter";
import { CreateAddressRequest } from "./model/AddressState";
import { Tour } from "./model/Tour";
import { ETA } from "./model/ETA";
import { Shipper } from "./model/Shipper";
import { BookRecurringOrder } from "../features/bookRecurring/model/BookRecurringOrder";

const apiName = "api";

export class ApiClient {
  // Zip
  checkZip = async (zip: string): Promise<CheckZipResponse> => {
    const res = await this.get(`/addresses/check/germany/${zip}/role/shipper`);
    return new CheckZipResponse(res);
  };

  // Addresses
  fetchAddresses = async (after?: string): Promise<Page<Address>> => {
    const queryStringParameters: any = {};
    if (!!after) {
      queryStringParameters["after"] = after;
    }
    return this.get<Page<Address>>(
      "/addresses/role/shipper",
      queryStringParameters
    );
  };

  fetchAllAddresses = async (): Promise<Address[]> => {
    return new Promise((resolve, reject) => {
      let cursor: string | undefined;
      const results: Address[] = [];
      doWhilst(
        async (cb) => {
          const page = await this.fetchAddresses(cursor);
          results.push(...page.results);
          cursor = page.cursors.after;
          cb();
        },
        () => !!cursor,
        (err) => {
          if (err) reject(err);
          resolve(results);
        }
      );
    });
  };

  fetchAddress = async (id: string): Promise<Address> => {
    return this.get<Address>(`/addresses/shipper/${id}`);
  };

  createAddress = async (request: CreateAddressRequest): Promise<Address> => {
    return this.post<Address>("/addresses/role/shipper", request);
  };

  // Quoting
  getQuote = async (body: QuoteRequest): Promise<QuoteResponse> => {
    return this.post<QuoteResponse>("/quote", body);
  };

  // Invoices
  fetchInvoices = async (
    filter: InvoicesFilter,
    after?: string
  ): Promise<Page<Invoice>> => {
    const queryStringParameters: any = {
      status: filter,
    };
    if (!!after) {
      queryStringParameters["after"] = after;
    }
    return this.get("/invoices/shipper", queryStringParameters);
  };

  fetchInvoice = async (id: string): Promise<Invoice> => {
    return this.get<Invoice>(`/invoices/${id}/shipper`);
  };

  // Orders
  fetchOrders = async (
    filter: OrdersFilter,
    after?: string
  ): Promise<Page<Order>> => {
    const queryStringParameters: any = {
      filter: filter,
    };
    if (!!after) {
      queryStringParameters["after"] = after;
    }
    return this.get<Page<Order>>("/orders/shipper", queryStringParameters);
  };

  fetchOrder = async (id: string): Promise<Order> => {
    return this.get<Order>(`/orders/${id}/shipper`);
  };

  fetchOrderStatus = async (id: string): Promise<any> => {
    return this.get(`/orders/${id}/status/shipper`);
  };

  createOrder = async (body: CreateOrderRequest): Promise<Order> => {
    return this.post("", {});
    return this.post<Order>("/orders/shipper", body);
  };

  searchOrders = async (query: string): Promise<Order[]> => {
    return new Promise(async (resolve, reject) => {
      try {
        const results = (await this.get(
          `/search/role/shipper?query=${query}`
        )) as Order[];
        resolve(results);
      } catch (error) {
        resolve([]);
      }
    });
  };

  createRecurringOrder = async (
    body: BookRecurringOrder
  ): Promise<BookRecurringOrder> => {
    return this.post<BookRecurringOrder>("/orders/shipper/recurring", body);
  };

  // update documents for a shipment
  updateDocuments = async (
    shipmentId: string,
    pickupDocuments: string[],
    dropoffDocuments: string[]
  ): Promise<void> => {
    return this.put(`/orders/shipment/${shipmentId}`, {
      pickupDocuments,
      dropoffDocuments,
    });
  };

  // Tours
  fetchTour = async (tourId: string): Promise<Tour> => {
    return this.get<Tour>(`/tours/shipper/${tourId}`);
  };

  // ETA
  fetchETA = async (tourId: string): Promise<ETA[]> => {
    return this.get<ETA[]>(`/tours/shipper/${tourId}/eta`);
  };

  // Locations
  fetchLiveTrackingLocation = async (
    tourId: string
  ): Promise<LiveTrackingLocation> => {
    return this.get<LiveTrackingLocation>(
      `/locations/tour/${tourId}/role/shipper`
    );
  };

  // Shipper Account Info
  fetchShipperAccount = async (): Promise<Shipper> => {
    return this.get(`/shippers/me`);
  };

  // Shipper Account Info
  sendSessionStart = async (): Promise<void> => {
    return this.get(`/shippers/session/start`);
  };

  // Get the presigned URL for the document upload
  getStopDocumentUploadUrl = async (
    orderId: string,
    fileExtension: string
  ): Promise<UploadResponse> => {
    return this.post(`/documents/order/${orderId}`, {
      fileExtension: fileExtension,
      type: "stop",
    });
  };

  // Private Functions
  private get = async <T>(
    path: string,
    queryStringParameters: object | undefined = undefined
  ): Promise<T> => {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();

    const init: any = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      response: true,
    };
    if (!!queryStringParameters) {
      init.queryStringParameters = queryStringParameters;
    }

    const res = await API.get(apiName, path, init);
    return res.data;
  };

  private async post<T>(
    path: string,
    body: any,
    queryStringParameters: object | undefined = undefined
  ): Promise<T> {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();

    const init: any = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: body,
      response: true,
    };
    if (!!queryStringParameters) {
      init.queryStringParameters = queryStringParameters;
    }

    const res = await API.post(apiName, path, init);
    return res.data;
  }

  private async put<T>(
    path: string,
    body: any,
    queryStringParameters: object | undefined = undefined
  ): Promise<T> {
    const session = await Auth.currentSession();
    const token = session.getIdToken().getJwtToken();

    const init: any = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: body,
      response: true,
    };
    if (!!queryStringParameters) {
      init.queryStringParameters = queryStringParameters;
    }

    const res = await API.put(apiName, path, init);
    return res.data;
  }
}
